import {
  CHAT_STATUS,
  CHAT_TYPE,
  GROUP_TYPE,
  IBroadcastChatData,
  IChatData,
} from "@home-health-notify/domain";
import CampaignIcon from "@mui/icons-material/Campaign";
import { ListItem, ListItemAvatar, ListItemButton } from "@mui/material";
import { useTranslation } from "hhn-commons";
import { useSnackbar } from "notistack";
import React from "react";
import { Link } from "react-router";
import DisplayAvatarImage from "../common/DisplayAvatarImage";
import { ROUTES } from "../routes";
import theme from "../theme";
import { getAvatarSize, GROUP_AVATAR_SIZE } from "../utils/AvatarUtils";
import {
  ChatListItemBody,
  ChatListItemTitle,
  ChatListItemWrapper,
  ListItemWrapper,
} from "./ChatList.style";
import { ChatListItemStatus } from "./ChatListItemStatus";
import { DeletedChatListItem } from "./DeletedChatListItem";

export type ChatListItemProps = {
  chatData: IChatData;
  isSelected: boolean;
  avatarText: string;
  title: string;
  image?: string;
  groupType: GROUP_TYPE;
  isHidden?: boolean;
};

function ChatListItem({
  chatData,
  isSelected,
  avatarText,
  title,
  image,
  groupType,
  isHidden = false,
}: ChatListItemProps): React.JSX.Element {
  const { t } = useTranslation("chat");
  const { enqueueSnackbar } = useSnackbar();
  let chatRoute: string = chatData.chatType;
  if (chatRoute === CHAT_TYPE.GROUP) {
    chatRoute = isHidden ? GROUP_TYPE.HIDDEN : GROUP_TYPE.NON_PATIENT;
  }
  const isBroadcast =
    (chatData as IBroadcastChatData).chatType === CHAT_TYPE.BROADCAST;
  const isDeleted = chatData.status === CHAT_STATUS.DELETED;
  const linkProps = isDeleted
    ? {
        onClick: (event: React.MouseEvent) => {
          event.preventDefault();
          enqueueSnackbar(t("groupDeletedToast"), { autoHideDuration: 3000 });
        },
      }
    : {
        component: Link,
        to: `../${ROUTES.CHATS}/${chatRoute.toLowerCase()}/${chatData.from}/message`,
      };
  const listItemProps = {
    disablePadding: true,
    divider: true,
    sx: { minHeight: "65px", overflowAnchor: "none" },
  };
  const content = (
    <ListItemButton
      disableGutters
      {...linkProps}
      selected={isSelected}
      sx={{
        padding: "10px 13px 10px 10px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <ListItemWrapper>
        <ListItemAvatar>
          <DisplayAvatarImage
            src={image}
            alt={avatarText}
            staticIcon={isBroadcast ? CampaignIcon : undefined}
            avatarSize={getAvatarSize(groupType, GROUP_AVATAR_SIZE.SMALL)}
          />
        </ListItemAvatar>
        <ChatListItemWrapper>
          <ChatListItemTitle
            sx={{ fontWeight: 500 }}
            data-testid="chat-name"
            noWrap
          >
            {title}
          </ChatListItemTitle>
          <ChatListItemBody
            variant="body2"
            color={theme.palette.grey[800]}
            noWrap
            data-testid="last-message"
          >
            {chatData.lastMessage?.body ?? ""}
          </ChatListItemBody>
        </ChatListItemWrapper>
      </ListItemWrapper>
      <ChatListItemStatus chatData={chatData} />
    </ListItemButton>
  );
  return isDeleted ? (
    <DeletedChatListItem {...listItemProps}>{content}</DeletedChatListItem>
  ) : (
    <ListItem {...listItemProps}>{content}</ListItem>
  );
}

export default ChatListItem;
